// Payments/CreatePaymentLinkForm.js

import React, { useState } from 'react';
import axiosInstance from "../../utils/axiosInstance";
import {
  Box, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Grid, Card, CardContent, Switch, FormControlLabel, Chip, Alert, Divider,
  Accordion, AccordionSummary, AccordionDetails, IconButton, Tooltip,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Paper,
  Stack, alpha, useTheme, InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PreviewIcon from '@mui/icons-material/Preview';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';

const CreatePaymentLinkForm = ({ onSuccess, permission }) => {
  const theme = useTheme();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    disclaimer: '',
    payment_type: 'others',
    recurring_type: 'onetime',
    suggested_amounts: ['10.00', '25.00', '50.00', '100.00'],
    purposes: ['General'],
    enable_for_chatbot: false, 
    custom_amount_enabled: true,
    collect_customer_info: true,
    payment_methods: {
      stripe: true,
      paypal: false
    },
    payment_frequencies: {
      onetime: true,
      recurring: false
    },
    processing_fee_handling: 'absorbed',
    processing_fee_percentage: 2.9,
    fixed_fee: 0.30,
    thank_you_message: 'Thank you for your payment!',
    redirect_url: '',
    expires_at: '',
    is_active: true
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSuggestedAmountChange = (index, value) => {
    const newAmounts = [...formData.suggested_amounts];
    newAmounts[index] = value;
    setFormData(prev => ({
      ...prev,
      suggested_amounts: newAmounts
    }));
  };

  const addSuggestedAmount = () => {
    setFormData(prev => ({
      ...prev,
      suggested_amounts: [...prev.suggested_amounts, '']
    }));
  };

  const handlePurposeChange = (index, value) => {
    const newPurposes = [...formData.purposes];
    newPurposes[index] = value;
    setFormData(prev => ({
      ...prev,
      purposes: newPurposes
    }));
  };

  const addPurpose = () => {
    setFormData(prev => ({
      ...prev,
      purposes: [...prev.purposes, '']
    }));
  };

  const removePurpose = (index) => {
    setFormData(prev => ({
      ...prev,
      purposes: prev.purposes.filter((_, i) => i !== index)
    }));
  };

  const removeSuggestedAmount = (index) => {
    setFormData(prev => ({
      ...prev,
      suggested_amounts: prev.suggested_amounts.filter((_, i) => i !== index)
    }));
  };

  const calculateProcessingFee = (amount) => {
    const baseAmount = parseFloat(amount || 0);
    const percentageFee = (baseAmount * formData.processing_fee_percentage) / 100;
    const totalFee = percentageFee + formData.fixed_fee;
    return totalFee.toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await axiosInstance.post(
        `/payments/create-advanced-payment-link`,
        formData
      );

      setGeneratedLink(`${window.location.origin}/pay/${response.data.token}`);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create payment link');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink).then(() => {
      alert('Payment link copied to clipboard!');
    });
  };

  const generateQRCode = () => {
    alert('QR Code generation would be implemented here');
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {/* Basic Information */}
      <Card 
        variant="outlined" 
        sx={{ 
          mb: 3, 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            <DescriptionIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600} color="textPrimary">
              Basic Information
            </Typography>
          </Stack>
          
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Payment Title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Mosque Donation, Course Fee, Membership"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required size="small">
                <InputLabel>Payment Type</InputLabel>
                <Select
                  name="payment_type"
                  value={formData.payment_type}
                  label="Payment Type"
                  onChange={handleInputChange}
                >
                  <MenuItem value="donation">Donation</MenuItem>
                  <MenuItem value="signup">Signup/Registration</MenuItem>
                  <MenuItem value="subscription">Subscription</MenuItem>
                  <MenuItem value="others">Others</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            
            
            <Grid item xs={12} sm={6}>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  bgcolor: alpha(theme.palette.info.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
                }}
              >
                <Typography variant="caption" sx={{ mb: 1, fontWeight: 600, display: 'block' }} color="textPrimary">
                  Payment Frequencies
                </Typography>
                <Stack spacing={0.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={formData.payment_frequencies.onetime}
                        onChange={handleInputChange}
                        name="payment_frequencies.onetime"
                      />
                    }
                    label={<Typography variant="body2">One-time Payment</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={formData.payment_frequencies.recurring}
                        onChange={handleInputChange}
                        name="payment_frequencies.recurring"
                      />
                    }
                    label={<Typography variant="body2">Recurring Payment</Typography>}
                  />
                </Stack>
              </Paper>
            </Grid>

            {/* Purpose Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }} color="textPrimary">
                Payment Purposes (Categories)
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                Define purposes/categories for this payment. Customers will select one when paying.
              </Typography>
              
              <Grid container spacing={2}>
                {formData.purposes.map((purpose, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TextField
                        fullWidth
                        label={`Purpose ${index + 1}`}
                        value={purpose}
                        onChange={(e) => handlePurposeChange(index, e.target.value)}
                        placeholder="e.g., Donation, Zakat, Course Fee"
                        size="small"
                      />
                      <IconButton 
                        onClick={() => removePurpose(index)}
                        disabled={formData.purposes.length <= 1}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Grid>
                ))}
                
                {formData.purposes.length < 10 && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addPurpose}
                      fullWidth
                      size="small"
                      sx={{ height: '40px' }}
                    >
                      Add Purpose
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe what this payment is for..."
                size="small"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Disclaimer (Optional)"
                name="disclaimer"
                value={formData.disclaimer}
                onChange={handleInputChange}
                placeholder="Any terms, conditions, or disclaimers..."
                size="small"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Amount Configuration */}
      <Card 
        variant="outlined" 
        sx={{ 
          mb: 3, 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            <AttachMoneyIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600} color="textPrimary">
              Amount Configuration
            </Typography>
          </Stack>
          
          <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }} color="textPrimary">
            Suggested Amounts
          </Typography>
          
          <Grid container spacing={2}>
            {formData.suggested_amounts.map((amount, index) => (
              <Grid item xs={10} sm={6} key={index}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    fullWidth
                    type="number"
                    label={`Amount ${index + 1}`}
                    value={amount}
                    onChange={(e) => handleSuggestedAmountChange(index, e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    inputProps={{ step: "0.01", min: "0" }}
                    size="small"
                  />
                  <IconButton 
                    onClick={() => removeSuggestedAmount(index)}
                    disabled={formData.suggested_amounts.length <= 1}
                    size="small"
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Grid>
            ))}
            
            {formData.suggested_amounts.length < 6 && (
              <Grid item xs={6} sm={3}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addSuggestedAmount}
                  fullWidth
                  size="small"
                  sx={{ height: '40px' }}
                >
                  Add
                </Button>
              </Grid>
            )}
          </Grid>

          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={formData.custom_amount_enabled}
                onChange={handleInputChange}
                name="custom_amount_enabled"
              />
            }
            label={<Typography variant="body2">Allow custom amount input</Typography>}
            sx={{ mt: 2 }}
          />
        </CardContent>
      </Card>

      {/* Payment Methods & Processing Fees */}
      <Card 
        variant="outlined" 
        sx={{ 
          mb: 3, 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
            <PaymentIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600} color="textPrimary">
              Payment Methods & Processing Fees
            </Typography>
          </Stack>
          
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2, 
              mb: 3, 
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
            }}
          >
            <Typography variant="caption" sx={{ mb: 1, fontWeight: 600, display: 'block' }} color="textPrimary">
              Accepted Payment Methods
            </Typography>
            <Stack spacing={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={formData.payment_methods.stripe}
                    onChange={handleInputChange}
                    name="payment_methods.stripe"
                  />
                }
                label={<Typography variant="body2">Stripe (Credit/Debit Cards)</Typography>}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={formData.payment_methods.paypal}
                    onChange={handleInputChange}
                    name="payment_methods.paypal"
                  />
                }
                label={<Typography variant="body2">PayPal</Typography>}
              />
            </Stack>
          </Paper>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }} color="textPrimary">
            Processing Fee Handling
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Fee Handling</InputLabel>
            <Select
              name="processing_fee_handling"
              value={formData.processing_fee_handling}
              label="Fee Handling"
              onChange={handleInputChange}
            >
              <MenuItem value="absorbed">Organization absorbs fees</MenuItem>
              <MenuItem value="added">Always add fees to customer</MenuItem>
              <MenuItem value="user_choice">Let customer choose</MenuItem>
            </Select>
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Processing Fee %"
                name="processing_fee_percentage"
                value={formData.processing_fee_percentage}
                onChange={handleInputChange}
                inputProps={{ step: "0.1", min: "0", max: "10" }}
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Fixed Fee ($)"
                name="fixed_fee"
                value={formData.fixed_fee}
                onChange={handleInputChange}
                inputProps={{ step: "0.01", min: "0" }}
                size="small"
              />
            </Grid>
          </Grid>

          {formData.suggested_amounts[0] && (
            <Alert 
              severity="info" 
              sx={{ mt: 2 }} 
              icon={<InfoOutlinedIcon fontSize="small" />}
            >
              <Typography variant="body2">
                Example: ${formData.suggested_amounts[0]} + ${calculateProcessingFee(formData.suggested_amounts[0])} fee = ${(parseFloat(formData.suggested_amounts[0] || 0) + parseFloat(calculateProcessingFee(formData.suggested_amounts[0]))).toFixed(2)} total
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Enable for Chatbot Integration - NEW CARD SECTION */}
      <Card 
        variant="outlined" 
        sx={{ 
          mb: 3, 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <SettingsIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600} color="textPrimary">
              Chatbot Integration
            </Typography>
          </Stack>

          <Paper 
            variant="outlined" 
            sx={{ 
              p: 2.5, 
              bgcolor: alpha(theme.palette.info.main, 0.04),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={formData.enable_for_chatbot}
                  onChange={handleInputChange}
                  name="enable_for_chatbot"
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Enable for Chatbot Integration
                  </Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                    Allow this payment link to be used within chatbot conversations. This setting is not visible to customers.
                  </Typography>
                </Box>
              }
              sx={{ 
                m: 0,
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': {
                  ml: 1
                }
              }}
            />
          </Paper>

          {formData.enable_for_chatbot && (
            <Alert 
              severity="success" 
              sx={{ mt: 2 }} 
              icon={<InfoOutlinedIcon fontSize="small" />}
            >
              <Typography variant="body2">
                This payment link will be available for use in your chatbot. Customers interacting with your chatbot will be able to access this payment option.
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Advanced Settings */}
      <Accordion 
        sx={{ 
          mb: 3, 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
          '&:before': { display: 'none' }
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            '& .MuiAccordionSummary-content': { 
              alignItems: 'center' 
            }
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <SettingsIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600} color="textPrimary">
              Advanced Settings
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 2 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={formData.collect_customer_info}
                    onChange={handleInputChange}
                    name="collect_customer_info"
                  />
                }
                label={
                  <Typography variant="body2">
                    Collect {formData.payment_type === 'donation' ? 'Donor' : 'Customer'} Information (Name & Email)
                  </Typography>
                }
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Thank You Message"
                name="thank_you_message"
                value={formData.thank_you_message}
                onChange={handleInputChange}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Redirect URL (Optional)"
                name="redirect_url"
                value={formData.redirect_url}
                onChange={handleInputChange}
                placeholder="https://yoursite.com/thank-you"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Expires At (Optional)"
                name="expires_at"
                value={formData.expires_at}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* Submit Button */}
      {permission.canAdd && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{ flex: 1 }}
              size="large"
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Create Payment Link'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => setShowPreview(true)}
              size="large"
            >
              Preview
            </Button>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {generatedLink && (
            <Card 
              sx={{ 
                mb: 3, 
                bgcolor: alpha(theme.palette.success.main, 0.08),
                border: `2px solid ${theme.palette.success.main}`,
                borderRadius: 2
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'success.dark', fontWeight: 600 }}>
                  Payment Link Created Successfully!
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    value={generatedLink}
                    InputProps={{ readOnly: true }}
                    size="small"
                    variant="outlined"
                  />
                  <Tooltip title="Copy Link">
                    <IconButton onClick={handleCopyLink} color="success" size="small">
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Generate QR Code">
                    <IconButton onClick={generateQRCode} color="success" size="small">
                      <QrCodeIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Typography variant="body2" color="textSecondary">
                  Share this link with your customers or embed it on your website.
                </Typography>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreview} onClose={() => setShowPreview(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={600} color="textPrimary">Payment Link Preview</Typography>
        </DialogTitle>
        <DialogContent>
          <PaymentLinkPreview formData={formData} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowPreview(false)} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Preview Component
const PaymentLinkPreview = ({ formData }) => {
  const theme = useTheme();
  
  return (
    <Card variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h5" component="h1" gutterBottom fontWeight={700} color="textPrimary">
          {formData.title || 'Payment Title'}
        </Typography>
        
        {formData.description && (
          <Typography variant="body2" sx={{ mb: 3 }} color="textSecondary">
            {formData.description}
          </Typography>
        )}

        {formData.purposes && formData.purposes.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }} color="textPrimary">
              Select Purpose:
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Purpose</InputLabel>
              <Select
                label="Purpose"
                defaultValue={formData.purposes[0]}
              >
                {formData.purposes.map((purpose, index) => (
                  <MenuItem key={index} value={purpose}>
                    {purpose}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }} color="textPrimary">
          Select Amount:
        </Typography>
        
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {formData.suggested_amounts.map((amount, index) => (
            amount && (
              <Grid item xs={6} sm={3} key={index}>
                <Chip
                  label={`$${amount}`}
                  variant="outlined"
                  clickable
                  sx={{ 
                    width: '100%', 
                    height: 36, 
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}
                />
              </Grid>
            )
          ))}
        </Grid>

        {formData.custom_amount_enabled && (
          <TextField
            fullWidth
            type="number"
            label="Custom Amount"
            placeholder="Enter amount"
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            sx={{ mb: 3 }}
            size="small"
          />
        )}

        {formData.collect_customer_info && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }} color="textPrimary">
              {formData.payment_type === 'donation' ? 'Donor' : 'Customer'} Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Name" size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" type="email" size="small" />
              </Grid>
            </Grid>
          </Box>
        )}

        {formData.disclaimer && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 1 }}>
            <Typography variant="body2">{formData.disclaimer}</Typography>
          </Alert>
        )}

        <Button variant="contained" fullWidth size="large">
          {formData.payment_type === 'donation' ? 'Donate Now' : 'Pay Now'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default CreatePaymentLinkForm;